import React, { useState, useEffect } from "react";
import { useDeal } from "../lib/store";
import { Tile, Card, RecBadge, Logo, ScopeTag, ScenarioBar } from "../components/shared";
import { RollUpSummaryCard } from "../components/RollUpSummary";
import { RevenueSdeChart, RevenueMixChart } from "../components/charts";
import { firmApi, dealsApi } from "../lib/api";
import * as m from "../lib/model";
import { qsbsBump } from "../components/room/Qsbs";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { RoomActivityCard } from "../components/room/RoomActivityCard";
import { ThemeToggle } from "../components/ThemeToggle";
import { PlatformLink } from "../components/PlatformLink";
import { Plus } from "lucide-react";
import { TeamChips } from "../components/team/TeamChips";
import { SourcesUsesTables } from "../components/SourcesUsesTables";
import { TieOutBadge } from "../components/TieOutBadge";

export default function Dashboard() {
  const { M, V, scope, setField, dealId, setDealId, dealName, navigate, startNewDeal, setAsmpSection, scenario, setScenario, scenFlash } = useDeal();
  const consolidated = m.isRollupScope(V);
  const addonView = scope.startsWith("addon:");
  const viewTag = m.scopeLabel(M, scope);
  const [firmName, setFirmName] = useState("XYZ Ventures");
  const [saving, setSaving] = useState(false);
  useEffect(() => { firmApi.get().then((f) => f?.name && setFirmName(f.name)).catch(() => {}); }, []);
  const v = m.verdict(M);
  const dscr = m.dscrYr1(V);
  const quality = m.sdeQuality(M);
  const addbackPct = 100 - quality;

  const ex = m.exitReturns(V);
  const proforma = m.model5yr(V);
  const wf = m.lpWaterfall(V);
  const bump = qsbsBump(V);
  const holdYrs = m.effectiveHold(V);

  const saveDeal = async () => {
    setSaving(true);
    try {
      const payload = { name: dealName || M.dealName, model: M, verdict: v };
      if (dealId) { await dealsApi.update(dealId, payload); toast.success("Deal summary updated in pipeline"); }
      else { const d = await dealsApi.create({ ...payload, stage: "Underwriting" }); setDealId(d.id); toast.success("Deal summary saved — retrieve it under Pipeline"); }
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const dsTone = m.dscrTone(dscr);

  const su = m.sourcesUses(V);

  const recap = m.isRecap(V);

  const cf = [
    { d: "Adj. SDE", a: V.sde },
    { d: "Less: new owner salary", a: -V.salary },
    { d: "Less: CapEx", a: -V.capex },
    ...(V.mgmtFeePct > 0 ? [{ d: `Less: management fee (${V.mgmtFeePct}% ${V.mgmtFeeBasis === "Gross Revenue" ? "gross rev." : "EBITDA"})`, a: -m.mgmtFee(V) }] : []),
    ...(V.slbOn ? [{ d: "Less: leaseback rent", a: -m.annualRent(V) }] : []),
    { d: "CF available for debt service", a: m.cfads(V), bold: true },
    { d: "Less: debt service", a: -m.totalAnnualDS(V) },
    { d: "Free cash flow", a: m.fcfYr1(V), bold: true, tone: m.fcfTone(m.fcfYr1(V)) },
  ];

  const qualityIssue = addbackPct > 60;

  return (
    <div className={`page ${scenFlash ? "scen-flash" : ""}`} data-testid="page-dashboard">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="page-title" data-testid="deal-name">{M.dealName} · {M.address}{viewTag && <span className="view-tag" data-testid="dash-view-tag">{viewTag}</span>}</div>
          <div className="page-sub">{[M.industry, M.brokerListingId ? `Listing ${M.brokerListingId}` : null, M.brokerName, M.brokerPhone].filter(Boolean).join(" · ")}</div>
          <div style={{ marginTop: 8 }}>
            <span className="tag">{M.dealType}</span>
            {M.snAmt > 0 && <span className="tag">Seller Note</span>}
            {M.slbOn && <span className="tag">SLB Scenario</span>}
            {m.revenueTrend(M).declinePct > 5 ? <span className="tag">Revenue Declining</span> : <span className="tag">Revenue Growing</span>}
          </div>
          <div style={{ marginTop: 10 }}><TeamChips scope="deal" dealKey={M.dealKey || ""} dealName={dealName || M.dealName} label="Deal team" compact /></div>
        </div>
        <div className="dash-brand">
          <Logo height={34} testid="dashboard-logo" />
          <div className="dash-brand-txt">
            <div className="dash-firm" data-testid="dashboard-firm">{firmName}</div>
            <div className="dash-analyst" data-testid="dashboard-analyst">
              {M.analystContact && <div><b>{M.analystContact}</b></div>}
              {M.analystEmail && <div className="mono">{M.analystEmail}</div>}
              {M.analystPhone && <div className="mono">{M.analystPhone}</div>}
              {M.prepDate && <div>{M.prepDate}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-ctlbar" data-testid="dash-controls">
        <ScopeTag M={M} value={scope} onEdit={() => navigate("assumptions")} testid="dash-scope" />
        <ScenarioBar value={scenario} onChange={setScenario} />
        <div className="dash-ctlbar-actions">
          <button className="btn btn-sm" onClick={startNewDeal} data-testid="dash-new-deal"><Plus size={13} style={{ marginRight: 6 }} />Start New Deal</button>
          <ThemeToggle testid="dash-theme" />
          <TieOutBadge V={V} testid="dash-tieout" />
          <button className="btn btn-sm" onClick={() => navigate("dealroom")} data-testid="dash-share-room"><Share2 size={13} style={{ marginRight: 6 }} />Share with investors</button>
          <RecBadge verdict={v} testid="dashboard-verdict" />
        </div>
      </div>

      {qualityIssue && (
        <div className="banner warn" data-testid="quality-banner">
          <span>⚠</span>
          <span>SDE quality flag: {addbackPct.toFixed(0)}% of stated SDE is officer-salary / owner add-backs. Independent quality-of-earnings review recommended before IC.</span>
        </div>
      )}

      {addonView && (
        <div className="grid g4" style={{ marginBottom: 12 }} data-testid="addon-tiles">
          <Tile label={`${V.companyName} — Purchase Price`} value={m.fmt$(V.askPrice)} sub={`${m.fmtX(V.sde ? V.askPrice / V.sde : 0)} × EBITDA · Roll-Up terms`} testid="tile-addon-price" />
          <Tile label="Add-on EBITDA" value={m.fmt$(V.sde)} testid="tile-addon-sde" />
          <Tile label="Senior debt + seller note" value={m.fmt$(m.loanAmt(V) + (V.snAmt || 0))} sub={`${V.rate}% senior`} testid="tile-addon-debt" />
          <Tile label="Sponsor equity" value={m.fmt$(su.equity)} sub={`${m.fmt$(m.rolloverAmt(V))} seller rollover`} testid="tile-addon-equity" />
          <Tile label="DSCR Year 1" value={m.fmtX(dscr)} tone={dsTone} testid="tile-addon-dscr" />
          <Tile label="Est. revenue (platform margin)" value={m.fmt$(V.ttmRevenue)} testid="tile-addon-rev" />
          <Tile label="Free cash flow Yr 1" value={m.fmt$(m.fcfYr1(V))} tone={m.fcfTone(m.fcfYr1(V))} testid="tile-addon-fcf" />
          <Tile label="Exit multiple" value={m.fmtX(V.exitMult)} sub="platform exit assumption" testid="tile-addon-exit" />
        </div>
      )}
      {consolidated && <div style={{ marginBottom: 12 }}><RollUpSummaryCard V={V} onRollUp={() => navigate("rollup")} /></div>}
      {!addonView && <>
      <div className={`grid ${recap ? "g5" : "g3"}`} style={{ marginBottom: 20 }}>
        <div className="tile" data-testid="tile-ask">
          <div className="label">{recap ? "Recap Valuation (EV)" : "Asking Price"}</div>
          <div className="money-input">
            <span className="cur">$</span>
            <input
              className="tile-input mono"
              type="text"
              inputMode="numeric"
              value={M.askPrice ? Number(M.askPrice).toLocaleString("en-US") : ""}
              placeholder="Not listed"
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, "");
                setField("askPrice", digits === "" ? 0 : Number(digits));
              }}
              data-testid="ask-price-input"
            />
          </div>
          <div className="sub">{recap ? `Recap EV · ${m.fmtX(m.recapCfg(M).recapMult)} × EBITDA` : M.askPrice ? `${m.fmt$(M.askPrice)} · editable` : "No asking price — enter your offer"}</div>
        </div>
        <Tile label="Stated EBITDA" value={m.fmt$(M.sde)} sub={`${addbackPct.toFixed(0)}% add-backs`} testid="tile-sde" />
        {recap
          ? <Tile label="New LP Equity Raised" value={m.fmt$(su.equity)} sub={`${m.fmt$(m.recapModel(M).warChestTotal)} war chest · ${m.fmt$(m.recapModel(M).cashOut)} legacy cash-out`} tone="go" testid="tile-nac" />
          : <Tile label={M.slbOn ? "Net Acq. Cost (SLB)" : "Net Acquisition Cost"} value={M.askPrice ? m.fmt$(m.nac(M)) : "—"} sub={M.slbOn ? "After SLB proceeds" : "No SLB"} testid="tile-nac" />}
        {!recap && <Tile label="Adj. Multiple" value={M.askPrice && M.sde ? m.fmtX(m.nac(M) / M.sde) : "—"} sub="NAC / SDE" testid="tile-mult" />}
        <Tile label="TTM Revenue" value={m.fmt$(M.ttmRevenue)} testid="tile-rev" />
        <Tile label="Gross Margin %" value={m.grossMargin(M) ? m.fmtPct(m.grossMargin(M)) : "—"} sub={m.grossMargin(M) ? undefined : "set it on Assumptions"} testid="tile-gm" />
      </div>
      </>}

      <div className="grid g2 dash-cols">
        <div className="dash-col">
        <Card title={consolidated ? "Sources & Uses — Consolidated Roll-Up" : addonView ? `Sources & Uses — ${V.companyName}` : "Sources & Uses"} testid="sources-uses" className="table-scroll dash-fill">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <button className="btn btn-sm" onClick={() => navigate("sourcesuses")} data-testid="dash-open-su">Open full Sources &amp; Uses</button>
            <button className="btn btn-primary btn-sm" onClick={saveDeal} disabled={saving} data-testid="dash-save-deal">{saving ? "Saving…" : dealId ? "Update Deal" : "Save Deal Summary"}</button>
          </div>
          <SourcesUsesTables V={V} />
        </Card>

        </div>

        <div className="dash-col">
          <Card title={viewTag ? `Returns Profile — ${viewTag}` : "Returns Profile"} testid="returns-profile">
            <div className="grid g3">
              <Tile label="Equity Invested" value={m.fmt$(ex.equityInvested)} sub={consolidated && ex.calledTotal > 0 ? `${m.fmt$(ex.equityInvested0)} at close + ${m.fmt$(ex.calledTotal)} calls` : undefined} />
              <Tile label="Equity MOIC" value={m.fmtX(ex.moic)} tone={m.moicTone(ex.moic)}
                sub={bump ? `QSBS +${m.fmtX(bump.x)} after tax` : undefined} testid="dash-eqmoic" />
              <Tile label="Deal IRR" value={isNaN(ex.irr) ? "n/a" : m.fmtPct(ex.irr)} tone={ex.irr >= 20 ? "go" : ex.irr >= 12 ? "amber" : "pass"}
                sub={bump && bump.bps > 0 ? `QSBS +${bump.bps} bps after tax` : undefined} testid="dash-dealirr" />
              <Tile label={`Exit EV (${holdYrs}yr)`} value={M.holdIndefinite ? "—" : m.fmt$(ex.exitEV)} />
              <Tile label="Equity at Exit" value={M.holdIndefinite ? "—" : m.fmt$(ex.equityAtExit)} />
              <Tile label="Cumulative FCF" value={m.fmt$(ex.totalFCF)} tone={ex.totalFCF >= 0 ? "go" : "pass"} />
            </div>
          </Card>

          <Card title={consolidated ? "Pro Forma — Consolidated Cash Flow" : "Pro Forma — Cash Flow"} testid="dash-proforma" className="table-scroll">
            <div className="dash-pf">
              {(proforma.length > 6 ? [proforma.slice(0, Math.ceil(proforma.length / 2)), proforma.slice(Math.ceil(proforma.length / 2))] : [proforma]).map((chunk, ci) => (
                <table className="tbl" key={ci}>
                  <thead><tr><th>Yr</th><th className="num">CFADS</th><th className="num">Debt Svc</th><th className="num">FCF</th><th className="num">DSCR</th></tr></thead>
                  <tbody>
                    {chunk.map((r) => (
                      <tr key={r.y}><td>Y{r.y}</td><td className="num">{m.fmt$(r.cfads)}</td><td className="num exp">{m.fmt$(-r.ds_y)}</td><td className={`num ${r.fcf >= 0 ? "pos" : "neg"}`}>{m.fmt$(r.fcf)}</td><td className={`num ${m.dscrTone(r.dscr) === "go" ? "pos" : m.dscrTone(r.dscr) === "pass" ? "neg" : "neu"}`}>{m.fmtX(r.dscr)}</td></tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          </Card>

          <Card title="Equity Distribution Summary (LP / GP)" testid="dash-equity-dist" className="dash-fill">
            <div className="grid g3">
              <Tile label="LP equity (waterfall basis)" value={m.fmt$(wf.lpEquity)} sub="incl. rolled legacy · ex-GP share" />
              <Tile label="LP MOIC" value={m.fmtX(wf.lpMoic)} tone={wf.lpMoic >= (M.moicTarget || 2) ? "go" : "amber"}
                sub={bump ? `QSBS +${m.fmtX(bump.x)} after tax${bump.unlevered ? " · unlevered" : ""}` : undefined} testid="dash-lpmoic" />
              <Tile label="LP IRR" value={isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)} tone={wf.lpIrr >= 15 ? "go" : "amber"}
                sub={bump && bump.bps > 0 ? `QSBS +${bump.bps} bps after tax` : undefined} testid="dash-lpirr" />
              <Tile label="Preferred Paid" value={m.fmt$(wf.prefTotal)} />
              <Tile label="Total LP Dist." value={m.fmt$(wf.lpTotal)} />
              <Tile label="GP Promote" value={m.fmt$(wf.gpTotal)} tone="go" />
            </div>
          </Card>
        </div>
      </div>

      <div style={{ marginTop: 12 }}><PlatformLink /></div>
      <div style={{ marginTop: 12 }}>
        <RoomActivityCard compact refreshKey={0} onShare={() => navigate("dealroom")} />
      </div>

      <div className="grid g2" style={{ marginTop: 12 }}>
        {(M.historicalYears || []).length > 1 && <Card title="Revenue & SDE Trend"><RevenueSdeChart hy={M.historicalYears} /></Card>}
        <Card title="Revenue Mix" testid="dash-revenue-mix">
          {(M.revenueMix || []).length
            ? <RevenueMixChart mix={M.revenueMix} />
            : <div className="bullet">No revenue mix recorded. Add the customer or service types on <button className="lnk" onClick={() => { navigate("assumptions"); setAsmpSection("revenue-mix"); }} data-testid="dash-mix-link">Assumptions → Revenue Mix</button> and it shows here and in the investor room.</div>}
        </Card>
      </div>

      <div className="grid g2" style={{ marginTop: 12 }}>
        {(M.historicalYears || []).length <= 1 ? (
          <Card title={(M.historicalYears || []).length ? `Trailing 12 Months — FY${M.historicalYears[0].year}` : "Historical Financial Summary"}>
            <div className="ir-metric-strip" data-testid="dash-hist-ttm" style={{ marginBottom: 0 }}>
              {[["Revenue", m.fmt$((M.historicalYears?.[0]?.revenue) || M.ttmRevenue)], ["Gross margin", M.historicalYears?.[0]?.gm ? m.fmtPct(m.pctOf(M.historicalYears[0].gm)) : m.fmtPct(m.grossMargin(M))],
                ["Net income", m.fmt$(M.historicalYears?.[0]?.netIncome || 0)], ["Adj. SDE", m.fmt$(M.historicalYears?.[0]?.sde || M.sde)]
              ].map(([k, v]) => <div className="ir-metric" key={k}><span>{k}</span><b className="mono">{v}</b></div>)}
            </div>
          </Card>
        ) : (
        <Card title="Historical Financial Summary" className="table-scroll">
          <table className="tbl">
            <thead><tr><th>Fiscal Year</th><th className="num">Revenue</th><th className="num">GM %</th><th className="num">Net Income</th><th className="num">Adj. SDE</th></tr></thead>
            <tbody>
              {(M.historicalYears || []).map((h) => (
                <tr key={h.year}><td>{h.year}</td><td className="num">{m.fmt$(h.revenue)}</td><td className="num">{h.gm ? m.fmtPct(m.pctOf(h.gm)) : "—"}</td><td className={`num ${h.netIncome >= 0 ? "pos" : "neg"}`}>{m.fmt$(h.netIncome)}</td><td className="num">{m.fmt$(h.sde)}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>)}

        <Card title={viewTag ? `Year 1 Cash Flow Snapshot — ${viewTag}` : "Year 1 Cash Flow Snapshot"}>
          <table className="tbl">
            <tbody>
              {cf.map((r) => (
                <tr key={r.d} className={r.bold ? "hl" : ""}><td style={{ fontWeight: r.bold ? 700 : 400 }}>{r.d}</td><td className={`num ${r.tone === "go" ? "pos" : r.tone === "pass" ? "neg" : r.a < 0 ? "exp" : ""}`} style={{ fontWeight: r.bold ? 700 : 400 }}>{m.fmt$(r.a)}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", padding: "10px", background: "var(--bg3)", borderRadius: 3 }}>
            <span style={{ fontSize: 12, color: "var(--text2)" }}>DSCR Year 1</span>
            <span className={`mono ${dsTone === "go" ? "pos" : dsTone === "pass" ? "neg" : "neu"}`} style={{ fontWeight: 700 }}>{m.fmtX(dscr)}</span>
          </div>
        </Card>
      </div>
    </div>
  );
}
